/**
 * Equipos Apple que no son iPhone — la lógica, sin React.
 *
 * Una MacBook, un iPad, un Watch o unos AirPods se cargaban escribiendo el
 * nombre a mano, y en la base convivían `MACBOOK AIR M4 13" 16GB 256GB` y
 * `MACBOOK AIR M4 15" 16 GB 256 GB`: el mismo tipo de equipo, dos formatos, y
 * un buscador que encuentra uno y no el otro. Acá cada cosa se elige con
 * botones —como el iPhone— y el nombre sale siempre igual.
 *
 * Las opciones no son una tabla cerrada modelo por modelo (Apple saca chips
 * todos los años y la tabla quedaría vieja antes que el mostrador): son los
 * valores que existen para cada línea. Lo que no está se carga con «Otro
 * equipo», que es el texto libre de siempre.
 */
import { etiquetaCapacidad } from "@/components/iphones/logica";

export type FamiliaApple = "MAC" | "IPAD" | "WATCH" | "AIRPODS" | "OTRO";

export interface EquipoApple {
  familia: FamiliaApple | "";
  /** MacBook Air, iPad Pro, Series 11, AirPods Pro 3… */
  linea: string;
  chip: string;
  /** En pulgadas para Mac e iPad ("13"), en milímetros para el Watch ("46"). */
  pantalla: string;
  ramGb: number | null;
  memoriaGb: number | null;
  /** iPad: "WIFI" o "WIFI + CELLULAR". Watch: "GPS" o "GPS + CELL". */
  conexion: string;
}

export const EQUIPO_APPLE_INICIAL: EquipoApple = {
  familia: "",
  linea: "",
  chip: "",
  pantalla: "",
  ramGb: null,
  memoriaGb: null,
  conexion: "",
};

/** El rubro donde vive cada familia en el catálogo. */
export const RUBRO_DE_FAMILIA: Record<Exclude<FamiliaApple, "OTRO">, string> = {
  MAC: "Mac",
  IPAD: "iPad",
  WATCH: "Watch",
  AIRPODS: "AirPods",
};

export const FAMILIAS: ReadonlyArray<{ valor: FamiliaApple; etiqueta: string }> = [
  { valor: "MAC", etiqueta: "Mac" },
  { valor: "IPAD", etiqueta: "iPad" },
  { valor: "WATCH", etiqueta: "Watch" },
  { valor: "AIRPODS", etiqueta: "AirPods" },
  { valor: "OTRO", etiqueta: "Otro equipo" },
];

interface Linea {
  valor: string;
  etiqueta: string;
  chips?: ReadonlyArray<string>;
  /** Vacío: esa línea no pregunta pantalla (Mac mini, AirPods). */
  pantallas?: ReadonlyArray<string>;
  conexiones?: ReadonlyArray<string>;
  /**
   * La RAM que viene en esa línea, en GB. Una sola lista para todas las Mac
   * le ofrecía a la MacBook Air 36, 48, 64 y 128 GB, que no existen.
   */
  ram?: ReadonlyArray<number>;
}

const CHIPS_MAC = ["M1", "M2", "M3", "M4", "M4 PRO", "M4 MAX", "M5", "M5 PRO", "M5 MAX"];
/**
 * La MacBook Air y el iMac vienen solo con el chip de base: ofrecerles M4 Pro
 * o M5 Max era dejar cargar un equipo que no existe.
 */
const CHIPS_MAC_BASE = ["M1", "M2", "M3", "M4", "M5"];
/** La MacBook Air y el iMac: de 8 GB (los M1 a M3) a 32 GB. */
const RAM_MAC_BASE = [8, 16, 24, 32];

export const LINEAS: Record<Exclude<FamiliaApple, "OTRO">, ReadonlyArray<Linea>> = {
  MAC: [
    {
      valor: "MACBOOK AIR",
      etiqueta: "MacBook Air",
      chips: CHIPS_MAC_BASE,
      pantallas: ["13", "15"],
      ram: RAM_MAC_BASE,
    },
    {
      valor: "MACBOOK PRO",
      etiqueta: "MacBook Pro",
      chips: CHIPS_MAC,
      pantallas: ["14", "16"],
      ram: [8, 16, 18, 24, 32, 36, 48, 64, 128],
    },
    {
      valor: "IMAC",
      etiqueta: "iMac",
      chips: CHIPS_MAC_BASE,
      pantallas: ["24"],
      ram: RAM_MAC_BASE,
    },
    { valor: "MAC MINI", etiqueta: "Mac mini", chips: CHIPS_MAC, ram: [8, 16, 24, 32, 48, 64] },
    {
      valor: "MAC STUDIO",
      etiqueta: "Mac Studio",
      chips: CHIPS_MAC,
      ram: [32, 36, 48, 64, 96, 128, 192, 256, 512],
    },
  ],
  IPAD: [
    {
      valor: "IPAD",
      etiqueta: "iPad",
      chips: ["A14", "A16"],
      pantallas: ["10.9", "11"],
      conexiones: ["WIFI", "WIFI + CELLULAR"],
    },
    {
      valor: "IPAD MINI",
      etiqueta: "iPad mini",
      chips: ["A15", "A17 PRO"],
      pantallas: ["8.3"],
      conexiones: ["WIFI", "WIFI + CELLULAR"],
    },
    {
      valor: "IPAD AIR",
      etiqueta: "iPad Air",
      chips: ["M1", "M2", "M3", "M4"],
      pantallas: ["11", "13"],
      conexiones: ["WIFI", "WIFI + CELLULAR"],
    },
    {
      valor: "IPAD PRO",
      etiqueta: "iPad Pro",
      chips: ["M2", "M4", "M5"],
      pantallas: ["11", "13"],
      conexiones: ["WIFI", "WIFI + CELLULAR"],
    },
  ],
  WATCH: [
    { valor: "SE 3", etiqueta: "SE 3", pantallas: ["40", "44"], conexiones: ["GPS", "GPS + CELL"] },
    {
      valor: "SERIES 11",
      etiqueta: "Series 11",
      pantallas: ["42", "46"],
      conexiones: ["GPS", "GPS + CELL"],
    },
    { valor: "ULTRA 3", etiqueta: "Ultra 3", pantallas: ["49"], conexiones: ["GPS + CELL"] },
    { valor: "SE 2", etiqueta: "SE 2", pantallas: ["40", "44"], conexiones: ["GPS", "GPS + CELL"] },
    {
      valor: "SERIES 10",
      etiqueta: "Series 10",
      pantallas: ["42", "46"],
      conexiones: ["GPS", "GPS + CELL"],
    },
    { valor: "ULTRA 2", etiqueta: "Ultra 2", pantallas: ["49"], conexiones: ["GPS + CELL"] },
  ],
  AIRPODS: [
    { valor: "AIRPODS 4", etiqueta: "AirPods 4" },
    { valor: "AIRPODS 4 ANC", etiqueta: "AirPods 4 con ANC" },
    // Los dos AirPods 5 traen ANC; lo que cambia es el estuche (USB-C o con
    // carga inalambrica, que ademas suma volumen en el tallo).
    { valor: "AIRPODS 5 ANC", etiqueta: "AirPods 5 (ANC)" },
    {
      valor: "AIRPODS 5 ANC + CARGA INALAMBRICA",
      etiqueta: "AirPods 5 (ANC) + carga inalámbrica",
    },
    { valor: "AIRPODS PRO 2", etiqueta: "AirPods Pro 2" },
    { valor: "AIRPODS PRO 3", etiqueta: "AirPods Pro 3" },
    { valor: "AIRPODS MAX", etiqueta: "AirPods Max" },
  ],
};

export const RAM_MAC = [8, 16, 24, 32, 36, 48, 64, 128];

/** La RAM que se ofrece para la línea elegida; sin línea, la lista de todas. */
export function ramDeLinea(e: EquipoApple): ReadonlyArray<number> {
  return lineaDe(e)?.ram ?? RAM_MAC;
}

/**
 * Los colores que se ofrecen con un toque para cada familia. El color es
 * opcional —la ficha de lista de precios no lo lleva— y «Otro» deja escribir
 * el que no está. Antes el formulario de Mac, iPad y Watch no preguntaba el
 * color, y el equipo con unidades en la mano se creaba sin él.
 */
export const COLORES_DE_FAMILIA: Partial<Record<FamiliaApple, ReadonlyArray<string>>> = {
  MAC: ["MIDNIGHT", "STARLIGHT", "SILVER", "SPACE GRAY", "SPACE BLACK", "SKY BLUE"],
  IPAD: ["SPACE GRAY", "SILVER", "STARLIGHT", "BLUE", "PURPLE", "PINK", "YELLOW"],
  WATCH: ["MIDNIGHT", "STARLIGHT", "SILVER", "JET BLACK", "ROSE GOLD", "NATURAL", "BLACK"],
};

/** Mac, iPad y Watch preguntan el color; los AirPods son blancos. */
export function preguntaColor(e: EquipoApple): boolean {
  return e.familia === "MAC" || e.familia === "IPAD" || e.familia === "WATCH";
}
export const DISCO_MAC = [256, 512, 1024, 2048, 4096];
export const MEMORIA_IPAD = [64, 128, 256, 512, 1024, 2048];

export function lineaDe(e: EquipoApple): Linea | null {
  if (e.familia === "" || e.familia === "OTRO") return null;
  return LINEAS[e.familia].find((l) => l.valor === e.linea) ?? null;
}

/**
 * Al cambiar algo de arriba, lo de abajo que ya no corresponde se borra: un
 * iPad Air no tiene pantalla de 8.3", y un Ultra no viene en 40 mm. Si se
 * dejara, el nombre armado describiría un equipo que no existe.
 */
export function ajustar(e: EquipoApple, cambios: Partial<EquipoApple>): EquipoApple {
  const nuevo = { ...e, ...cambios };
  if (cambios.familia !== undefined && cambios.familia !== e.familia)
    return { ...EQUIPO_APPLE_INICIAL, familia: nuevo.familia };
  const linea = lineaDe(nuevo);
  if (!linea) return nuevo;
  if (nuevo.chip && !(linea.chips ?? []).includes(nuevo.chip)) nuevo.chip = "";
  if (nuevo.ramGb !== null && linea.ram && !linea.ram.includes(nuevo.ramGb)) nuevo.ramGb = null;
  const pantallas = linea.pantallas ?? [];
  if (pantallas.length === 1) nuevo.pantalla = pantallas[0] ?? "";
  else if (nuevo.pantalla && !pantallas.includes(nuevo.pantalla)) nuevo.pantalla = "";
  const conexiones = linea.conexiones ?? [];
  if (conexiones.length === 1) nuevo.conexion = conexiones[0] ?? "";
  else if (nuevo.conexion && !conexiones.includes(nuevo.conexion)) nuevo.conexion = "";
  return nuevo;
}

/** Lo que falta contestar, en el orden en que se pregunta. `null` si está completo. */
export function faltaDelEquipoApple(e: EquipoApple): string | null {
  if (e.familia === "") return "¿Qué equipo es? (Mac, iPad, Watch, AirPods…)";
  if (e.familia === "OTRO") return null;
  const linea = lineaDe(e);
  if (!linea) return "Elegí cuál es.";
  if (linea.chips && !e.chip) return "Elegí el chip.";
  if ((linea.pantallas?.length ?? 0) > 1 && !e.pantalla)
    return e.familia === "WATCH" ? "Elegí el tamaño." : "Elegí la pantalla.";
  if (e.familia === "MAC" && e.ramGb === null) return "Elegí la memoria RAM.";
  if ((e.familia === "MAC" || e.familia === "IPAD") && e.memoriaGb === null)
    return e.familia === "MAC" ? "Elegí el disco." : "Elegí la memoria.";
  if ((linea.conexiones?.length ?? 0) > 1 && !e.conexion) return "¿WiFi o con celular?";
  return null;
}

function pulgadas(p: string): string {
  return p ? `${p}"` : "";
}

/**
 * El nombre del catálogo, siempre con el mismo formato:
 *
 *   MACBOOK AIR M4 13" 16/256 GB
 *   IPAD AIR M4 11" 128 GB WIFI
 *   WATCH SERIES 11 46MM GPS + CELL
 *   AIRPODS PRO 3
 *
 * La RAM y el disco de una Mac van como en los celulares, «16/256 GB»: dos
 * números sueltos («16GB 256GB») no dicen cuál es cuál.
 * Vacío mientras falte algo: un nombre a medias no se guarda.
 */
export function nombreDeEquipoApple(e: EquipoApple): string {
  if (e.familia === "" || e.familia === "OTRO" || faltaDelEquipoApple(e)) return "";
  switch (e.familia) {
    case "MAC": {
      const disco = etiquetaCapacidad(e.memoriaGb ?? 0);
      const [numero, unidad] = disco.split(" ");
      return [e.linea, e.chip, pulgadas(e.pantalla), `${e.ramGb}/${numero} ${unidad}`]
        .filter(Boolean)
        .join(" ");
    }
    case "IPAD":
      return [
        e.linea,
        e.chip,
        pulgadas(e.pantalla),
        etiquetaCapacidad(e.memoriaGb ?? 0),
        e.conexion,
      ]
        .filter(Boolean)
        .join(" ");
    case "WATCH":
      return ["WATCH", e.linea, `${e.pantalla}MM`, e.conexion].filter(Boolean).join(" ");
    case "AIRPODS":
      return e.linea;
  }
}
